import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Message {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
}

interface FAQ {
  question: string;
  answer: string;
  keywords: string[];
}

const faqs: FAQ[] = [
  {
    question: "Quais serviços a FaliFe Tecnologia oferece?",
    answer: "Oferecemos desenvolvimento de aplicativos móveis, sistemas web, consultoria em tecnologia, automação de processos, design UX/UI e soluções personalizadas para empresas de todos os tamanhos.",
    keywords: ["serviços", "oferece", "fazem", "trabalho", "especialidade"]
  },
  {
    question: "Como posso entrar em contato?",
    answer: "Você pode nos contatar através do WhatsApp, e-mail (contato@falifetecnologia.com.br) ou preenchendo o formulário em nosso site. Respondemos rapidamente!",
    keywords: ["contato", "telefone", "email", "whatsapp", "falar"]
  },
  {
    question: "Vocês desenvolvem aplicativos móveis?",
    answer: "Sim! Desenvolvemos aplicativos nativos e híbridos para iOS e Android, utilizando as melhores tecnologias como React Native, Flutter e desenvolvimento nativo.",
    keywords: ["app", "aplicativo", "móvel", "android", "ios", "mobile"]
  },
  {
    question: "Qual é o prazo de entrega de um projeto?",
    answer: "O prazo varia conforme a complexidade do projeto. Projetos simples podem ficar prontos em 2-4 semanas, enquanto sistemas mais complexos podem levar de 2-6 meses. Fazemos uma análise detalhada antes de definir prazos.",
    keywords: ["prazo", "entrega", "tempo", "demora", "quando"]
  },
  {
    question: "Vocês fazem manutenção após a entrega?",
    answer: "Sim! Oferecemos suporte técnico e manutenção contínua. Temos planos de manutenção mensal que incluem atualizações, correções e melhorias no sistema.",
    keywords: ["manutenção", "suporte", "após", "entrega", "correção", "atualização"]
  },
  {
    question: "Trabalham com e-commerce?",
    answer: "Sim! Criamos lojas virtuais completas com sistema de pagamento, gestão de estoque, painel administrativo e integração com principais gateways de pagamento do mercado.",
    keywords: ["ecommerce", "loja", "virtual", "vendas", "online", "pagamento"]
  },
  {
    question: "Quais tecnologias vocês usam?",
    answer: "Utilizamos tecnologias modernas como React, Node.js, Python, PHP, Flutter, React Native, MySQL, PostgreSQL, MongoDB e soluções em nuvem como AWS e Google Cloud.",
    keywords: ["tecnologia", "linguagem", "framework", "react", "node", "python"]
  },
  {
    question: "Fazem sites responsivos?",
    answer: "Claro! Todos os nossos sites são responsivos e otimizados para funcionar perfeitamente em computadores, tablets e smartphones, garantindo uma excelente experiência do usuário.",
    keywords: ["responsivo", "mobile", "celular", "tablet", "adaptável"]
  }
];

const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage: Message = {
        id: '1',
        text: 'Olá! 👋 Sou o assistente virtual da FaliFe Tecnologia.\n\nEscolha uma das opções abaixo ou digite sua pergunta:',
        isBot: true,
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
      setShowQuickActions(true);
    }
  }, [isOpen, messages.length]);

  const findBestAnswer = (userMessage: string): string => {
    const message = userMessage.toLowerCase();
    
    // Busca por palavras-chave
    let bestAnswer = '';
    let maxMatches = 0;

    faqs.forEach(faq => {
      const matches = faq.keywords.filter(keyword => 
        message.includes(keyword.toLowerCase())
      ).length;
      
      if (matches > maxMatches) {
        maxMatches = matches;
        bestAnswer = faq.answer;
      }
    });

    if (maxMatches > 0) {
      return bestAnswer;
    }

    // Respostas para saudações
    if (message.includes('olá') || message.includes('oi') || message.includes('hey')) {
      return 'Olá! É um prazer falar com você. Como posso ajudá-lo hoje? 😊';
    }

    if (message.includes('obrigado') || message.includes('valeu')) {
      return 'De nada! Fico feliz em ajudar. Se tiver mais dúvidas, estarei aqui! 😊';
    }

    if (message.includes('tchau') || message.includes('até logo')) {
      return 'Até logo! Foi ótimo conversar com você. Volte sempre que precisar! 👋';
    }

    // Resposta padrão
    return 'Desculpe, não entendi sua pergunta. Posso ajudar com informações sobre nossos serviços, prazos, tecnologias, contato e muito mais. Tente reformular sua pergunta ou escolha uma das opções abaixo:\n\n• Serviços oferecidos\n• Como entrar em contato\n• Desenvolvimento de apps\n• Prazos de entrega\n• Tecnologias utilizadas';
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      isBot: false,
      timestamp: new Date()
    };

    setMessages((prev: Message[]) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setShowQuickActions(false);

    // Simula delay de digitação do bot
    setTimeout(() => {
      const botResponse = findBestAnswer(inputValue);
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: botResponse + '\n\n🔄 Escolha outra pergunta ou digite algo novo:',
        isBot: true,
        timestamp: new Date()
      };

      setMessages((prev: Message[]) => [...prev, botMessage]);
      setIsTyping(false);
      setShowQuickActions(true); // Mostra o menu novamente após a resposta
    }, 1000 + Math.random() * 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const QuickActions = () => (
    <div className="flex flex-wrap gap-2 mb-3">
      {[
        'Nossos serviços',
        'Como entrar em contato',
        'Desenvolvimento de apps',
        'Prazos de entrega'
      ].map((action, index) => (
        <button
          key={index}
          onClick={() => {
            setInputValue(action);
            setShowQuickActions(false);
          }}
          className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs hover:bg-blue-200 transition-colors"
        >
          {action}
        </button>
      ))}
    </div>
  );

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Botão do Chat */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              className="block w-6 h-6"
            >
              ✕
            </motion.span>
          ) : (
            <motion.span
              key="chat"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              className="block w-6 h-6"
            >
              💬
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Janela do Chat */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="absolute bottom-16 right-0 w-80 h-96 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
              <h3 className="font-semibold">Assistente FaliFe</h3>
              <p className="text-sm text-blue-100">Online agora</p>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-[70%] p-3 rounded-2xl ${
                      message.isBot
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                    <span className="text-xs opacity-70 mt-1 block">
                      {message.timestamp.toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </motion.div>
              ))}

              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <div className="bg-gray-100 p-3 rounded-2xl">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            {showQuickActions && (
              <div className="px-4">
                <QuickActions />
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Digite sua mensagem..."
                  className="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <motion.button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim()}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  📤
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Chatbot;
